import React, { useContext, useEffect, useState } from 'react';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { i18n } from 'src/common/i18n/i18n';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { IconLock, IconPublic, IconDetails } from 'src/common/images';
import {
    getAccountActions,
    getAccountsStatusMessage,
    getKeywordsFromWatchLists,
    multiMarking,
} from 'src/common/utils/utils';
import { CustomList, ProfileInfo } from 'src/common/components';
import { Connection } from 'src/common/models/common-types';
import { COLORS, connectionTags, reactionIcons } from 'src/common/constants/constants';
import { ErrorBoundary } from 'react-error-boundary';
import { Tooltip } from '@cellebrite/saas-ui-common';
import Popup from 'src/common/components/Popup/Popup';
import IconButton from '@mui/material/IconButton';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import TagsManager from 'src/common/components/TagsManager/TagsManager';
import TagsViewer from 'src/common/components/TagsViewer/TagsViewer';
import { ConnectionInformationDialog } from 'src/common/dialogs';
import _ from 'lodash';
import MarkedText from 'src/common/components/MarkedText/MarkedText';
import { Element } from 'react-scroll';
import MultiMarkedText from '../../../../../../../../../../common/components/MultiMarkedText/MultiMarkedText';
import ViewWatchLists from '../../../../../../../../../../common/components/ViewWatchLists/ViewWatchLists';

export interface ConnectionCardProps {
    account: Connection;
    section: string;
    onCardClick?: any;
    styles?: SxProps;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({ account, section, onCardClick, styles }) => {
    const { dialog, collectionData, adminData, reportData } = useContext(GlobalContext);
    const [showActions, setShowActions] = useState(false);
    const [actionsFocused, setActionsFocused] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [finalAccount, setFinalAccount]: any = useState(account || null);
    const [accountStateMessages] = useState(
        getAccountsStatusMessage(finalAccount.public, finalAccount.site.toUpperCase()),
    );
    const [interactionsColumns, setInteractionsColumns] = useState({ firstColumn: [], secondColumn: [] });
    const [showTag, setShowTag] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const [tags, setTags] = useState(finalAccount.tags || []);

    useEffect(() => setTags(account.tags || []), [account]);
    useEffect(() => {
        const updatedAccount = _.cloneDeep(account);
        updatedAccount.tags = tags || [];
        setFinalAccount(updatedAccount);
    }, [tags]);
    useEffect(() => {
        const result = { firstColumn: [], secondColumn: [] };
        if (finalAccount.interactions) {
            finalAccount.interactions.plural
                .sort((a, b) => b.count - a.count)
                .map((item, index) => {
                    if (item.type === 'REACTED' && item.reactions) {
                        return (
                            <Tooltip
                                key={index}
                                styles={{ p: 0 }}
                                placement="right"
                                content={
                                    <Box sx={{ minWidth: 44 }}>
                                        {item.reactions && (
                                            <>
                                                {Object.entries(item.reactions).map(([reaction, count]: any) => {
                                                    const Icon = reactionIcons[reaction] || reactionIcons['like'];
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                            }}
                                                            key={reaction}
                                                        >
                                                            <Icon /> <Typography variant="s16w500">{count}</Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </Box>
                                }
                                children={
                                    <Typography key={item.type} variant="s12w500">{`${item.count} ${i18n.translate(
                                        connectionTags[item.type] || item.type,
                                    )}`}</Typography>
                                }
                            />
                        );
                    }
                    return (
                        <Typography key={item.type} variant="s12w500">{`${item.count} ${i18n.translate(
                            connectionTags[item.type] || item.type,
                        )}`}</Typography>
                    );
                })
                .forEach((elem, i) => (i < 3 ? result.secondColumn.push(elem) : result.firstColumn.push(elem)));
        }
        setInteractionsColumns(result);
    }, [finalAccount.interactions]);

    function fallbackRender() {
        return <></>;
    }

    function getShortenedList(list: any) {
        const MAX_DISPLAYED_ITEMS = 3;
        let result = [];
        if (list?.length > MAX_DISPLAYED_ITEMS) {
            const plusItems = [];
            list.forEach((item: any, index: any) =>
                index < MAX_DISPLAYED_ITEMS ? result.push(item) : plusItems.push(item),
            );
            result.push({ type: `+${list.length - MAX_DISPLAYED_ITEMS}`, tooltipItems: plusItems });
            return result;
        } else {
            result = list;
        }

        return result;
    }

    const getObject = (type) => {
        const obj = {};
        obj[`& mark:nth-of-type(${reportData.currentFindLink?.index + 1})`] =
            account.identifier === reportData.currentFindLink?.id &&
            type === reportData.currentFindLink?.type &&
            reportData.currentFindLink?.section === section
                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                : null;
        return obj;
    };

    const accountKeywords = getKeywordsFromWatchLists(
        finalAccount.watchlist,
        reportData.currentHighlightsWatchlist?.id,
    );

    const getBorderColor = () => {
        let result;
        if (reportData.displayHighlights) {
            result = account.identifier === reportData.currentHighlightsLink?.id ? COLORS.NEGATIVE : '#D7DCE1';
        } else {
            result = account.identifier === reportData.currentFindLink?.id ? COLORS.WARNING_LIGHTEST : '#D7DCE1';
        }
        return result;
    };

    return (
        <ErrorBoundary fallbackRender={fallbackRender}>
            <Box
                sx={{
                    p: '18px',
                    width: '33%',
                    display: 'flex',
                    height: '196px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    position: 'relative',
                    boxSizing: 'border-box',
                    border: `1px solid ${getBorderColor()}`,
                    ...styles,
                }}
                onMouseEnter={() => {
                    setShowTag(true);
                    setShowActions(true);
                }}
                onMouseLeave={() => {
                    setShowTag(false);
                    setShowActions(false);
                }}
                onClick={() =>
                    actionsFocused
                        ? null
                        : onCardClick
                          ? onCardClick?.(tags)
                          : dialog.open(ConnectionInformationDialog, {
                                styles: { width: '910px', height: '571px' },
                                account: finalAccount,
                                section: section,
                                onClose: (tags) => setTags(tags),
                            })
                }
                className="background-color-hover-mho"
            >
                <Element style={{ position: 'absolute' }} name={account.identifier + section}></Element>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: 'calc(100% - 64px)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, height: 24, columnGap: 1 }}>
                        {reportData.collection?.taggable && <TagsViewer checkedTags={tags} />}
                        {!!finalAccount.watchlist?.length && <ViewWatchLists watchLists={finalAccount.watchlist} />}
                        {finalAccount.interactions && (
                            <CustomList
                                gap={{ width: 1, height: 1 }}
                                list={getShortenedList(finalAccount.interactions.singular).map((item) =>
                                    item.tooltipItems ? (
                                        <Tooltip
                                            content={
                                                <CustomList
                                                    gap={{ width: 1, height: 1 }}
                                                    list={item.tooltipItems.map((tooltipItem) => (
                                                        <Typography
                                                            key={tooltipItem.type}
                                                            variant="s14w700"
                                                            sx={{ px: 1, borderRadius: '8px' }}
                                                            className="background-color-mst color-mh"
                                                        >{`${i18n.translate(
                                                            connectionTags[tooltipItem.type] || tooltipItem.type,
                                                        )}`}</Typography>
                                                    ))}
                                                />
                                            }
                                        >
                                            <Typography
                                                key={item.type}
                                                variant="s14w700"
                                                sx={{ px: 1, borderRadius: '8px' }}
                                                className="background-color-mst color-mh"
                                            >{`${i18n.translate(connectionTags[item.type] || item.type)}`}</Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography
                                            key={item.type}
                                            variant="s14w700"
                                            sx={{ px: 1, borderRadius: '8px' }}
                                            className="background-color-mst color-mh"
                                        >{`${i18n.translate(connectionTags[item.type] || item.type)}`}</Typography>
                                    ),
                                )}
                            />
                        )}
                    </Box>
                    <ProfileInfo
                        key={finalAccount.name}
                        imageSrc={finalAccount.localProfilePicture}
                        topInfo={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            variant: 's16w500',
                                            sx: multiMarking(
                                                finalAccount.identifier,
                                                section,
                                                'name',
                                                reportData.currentHighlightsLink,
                                            ),
                                        }}
                                        searchTextArr={accountKeywords}
                                    >
                                        {finalAccount.name}
                                    </MultiMarkedText>
                                ) : (
                                    <MarkedText
                                        containerProps={{
                                            variant: 's16w500',
                                            sx: getObject('name'),
                                        }}
                                        searchText={reportData.find}
                                    >
                                        {finalAccount.name}
                                    </MarkedText>
                                )}
                                {typeof finalAccount.public === 'boolean' && (
                                    <Box>
                                        {finalAccount.public ? (
                                            <Tooltip
                                                placement="top"
                                                content={
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold' }}>
                                                            {accountStateMessages.title}
                                                        </Typography>
                                                        {accountStateMessages.explanationText && (
                                                            <Typography>
                                                                {accountStateMessages.explanationText}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            >
                                                <IconPublic className="fill-md" style={{ marginLeft: '13px' }} />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip
                                                placement="top"
                                                content={
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold' }}>
                                                            {accountStateMessages.title}
                                                        </Typography>
                                                        {accountStateMessages.explanationText && (
                                                            <Typography>
                                                                {accountStateMessages.explanationText}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            >
                                                <IconLock className="fill-md" style={{ marginLeft: '13px' }} />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        }
                        platform={finalAccount.site}
                        bottomInfo={
                            reportData.displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        sx: multiMarking(
                                            account.identifier,
                                            section,
                                            'userId',
                                            reportData.currentHighlightsLink,
                                        ),
                                    }}
                                    searchTextArr={accountKeywords}
                                >
                                    {finalAccount.userName || finalAccount.userId}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    containerProps={{
                                        sx: getObject('userId'),
                                    }}
                                    searchText={reportData.find}
                                >
                                    {finalAccount.userName || finalAccount.userId}
                                </MarkedText>
                            )
                        }
                    />
                    <Box sx={{ display: 'flex', pl: 8.1 }}>
                        {!!interactionsColumns.secondColumn.length && <Stack>{interactionsColumns.secondColumn}</Stack>}
                        {!!interactionsColumns.firstColumn.length && <Stack>{interactionsColumns.firstColumn}</Stack>}
                    </Box>
                </Box>
                {(showActions || actionsOpen || showTag || tagOpen) && (
                    <Box onMouseEnter={() => setActionsFocused(true)} onMouseLeave={() => setActionsFocused(false)}>
                        {getAccountActions(
                            finalAccount,
                            collectionData.createCollection.mutate,
                            { position: 'absolute' },
                            [
                                {
                                    name: i18n.translate('Show Details'),
                                    icon: <IconDetails />,
                                    onClick: () => onCardClick && onCardClick(tags),
                                },
                            ],
                            () => setActionsOpen(!actionsOpen),
                            adminData.isQuotaLimitReached,
                            reportData.collection?.taggable ? (
                                <Box sx={{ ml: 1, mr: 0.5, minWidth: '24px' }}>
                                    {(showTag || tagOpen) && (
                                        <Popup
                                            onToggle={() => {
                                                setTagOpen(!tagOpen);
                                            }}
                                            actionElement={(handleOpen, anchorRef, open) => {
                                                return (
                                                    <Box ref={anchorRef}>
                                                        <IconButton
                                                            sx={{
                                                                'backgroundColor': tagOpen
                                                                    ? COLORS.MUTED_DOUBLE_HOVER
                                                                    : null,
                                                                '&:hover': {
                                                                    backgroundColor: COLORS.MUTED_DOUBLE_HOVER,
                                                                },
                                                            }}
                                                            disableRipple
                                                            onClick={handleOpen}
                                                        >
                                                            <AddTagIcon
                                                                fill={open ? COLORS.BRAND : COLORS.MUTED_DARK}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            }}
                                            contentElement={(handleClose) => (
                                                <Box>
                                                    <TagsManager
                                                        setTags={setTags}
                                                        checkedTags={tags}
                                                        section={section}
                                                        elementId={finalAccount.identifier}
                                                    />
                                                </Box>
                                            )}
                                        />
                                    )}
                                </Box>
                            ) : null,
                        )}
                    </Box>
                )}
            </Box>
        </ErrorBoundary>
    );
};

export default ConnectionCard;
